// ---------------- Component for the sidebar of the application -------------------
// ----------------------------------------------------------------------------------

// import libraries and components
import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useAvailableTickers } from '../hooks/useAvailableTickers';
import { useBatchStockData } from '../hooks/useBatchStockData';
import { Link, useLocation } from 'react-router-dom'; // allows for the tab navigation
import './Sidebar.css'; // import the styling for the sidebar

// create the sidebar component
const Sidebar = ({ selectedStock, onSelectStock, timeframe = 'MAX' }) => { // receive the selected stock, the function to update the selected stock, and the timeframe as props
  // set state variables
  const [searchTerm, setSearchTerm] = useState(''); // track the search input
  const [sortBy, setSortBy] = useState('default'); // track how the user wants to sort the list
  const location = useLocation(); // get current route location

  // call the hooks to get the available tickers and their latest prices
  const { tickers: allTickers, loading } = useAvailableTickers(); 
  const { watchlistData, loading: pricesLoading } = useBatchStockData(allTickers, timeframe);

  // filter the list of tickers based on the search term
  const filteredTickers = allTickers.filter(ticker =>
    ticker.symbol.toLowerCase().includes(searchTerm.toLowerCase()) || 
    ticker.name.toLowerCase().includes(searchTerm.toLowerCase()) // filter the ticker based on the search term - check if the symbol or name includes the search term
  );

  // add the price to the filtered tickers
  const priceMap = Object.fromEntries(watchlistData.map(w => [w.symbol, w])); // convert the watchlist data into a array for easy access 

  const displayList = filteredTickers.map(ticker => ({ // create a new list of tickers with their prices and changes
    ...ticker, // unpack all the ticker properties
    price: priceMap[ticker.symbol]?.price || 0, // add the price to each ticker, default to 0 if not available
    change: priceMap[ticker.symbol]?.change || 0, // add the change percentage to each ticker, default to 0 if not available
  }));

  // sort the list by the selected sorting option
  const sortedList = [...displayList].sort((a, b) => {
    if (sortBy === 'price_desc') return b.price - a.price; // sort by price descending
    if (sortBy === 'price_asc') return a.price - b.price; // sort by price ascending
    if (sortBy === 'return_desc') return b.change - a.change; // sort by return descending
    if (sortBy === 'return_asc') return a.change - b.change; // sort by return ascending
    return a.symbol.localeCompare(b.symbol); // default = alphabetical
  });

  return ( // return the JSX for the sidebar component
    <div className="sidebar"> {/* main sidebar container - styles as the left panel */}
      <div className="sidebar-header"> {/* header section of the sidebar */}
        <h2>Quantitative Research & Strategy Validation Lab</h2> {/* Application title */}
      </div>
      <div className="tab-buttons"> {/* add in navigation tabs */}
        <Link // creates the clickable link / box for navigation
          to="/"
          className={`tab-btn${location.pathname === '/' ? ' active' : ''}`} // takes the user to the root pathway
        >
          Home
        </Link>

        <Link
          to="/alternative-data"
          className={`tab-btn${location.pathname === '/alternative-data' ? ' active' : ''}`} // takes the user to the alternative data tab and pathway
        >
          Alternative Data
        </Link>

        <Link
          to="/backtesting"
          className={`tab-btn${location.pathname === '/backtesting' ? ' active' : ''}`}
        >
          Backtesting
        </Link>

        <Link
          to="/research"
          className={`tab-btn${location.pathname === '/research' ? ' active' : ''}`}
        >
          Research
        </Link>

      </div>
      
      <div className="search-container"> {/* search input for filtering stocks */}
        <Search size={16} />
        <input
          type="text"
          placeholder="Search a Stock..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)} // update search term on input change
        />
      </div> 

      <div className="watchlist"> {/* watchlist section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
          <h3>
            {searchTerm // if there is a search term, show the number of results, otherwise show the total number of tickers in the watchlist
              ? `Results (${displayList.length})`
              : `Watchlist (${allTickers.length})`}
          </h3>
          {(loading || pricesLoading) && ( // if loading tickers or prices, show an updating message
            <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Updating...</span>
          )}
        </div>

        {/* add the selector to allow the user to determine how the list is sorted */}
        <div className="sort-selector">
          <label className="sort-label">Sort by:</label>
          <select
            className="sort-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="default">A → Z</option>
            <option value="price_desc">Price: High → Low</option>
            <option value="price_asc">Price: Low → High</option>
            <option value="return_desc">Return: High → Low</option>
            <option value="return_asc">Return: Low → High</option>
          </select>
        </div>

        {loading ? ( // if still loading the tickers, show a loading message
          <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '13px' }}>
            Loading tickers...
          </div> // if still loading the tickers, show a loading message

        ) : ( // if not loading, display the sorted list of stocks
          sortedList.map(stock => ( // loop through the sorted list of stocks to display each one in the sidebar
            <div
              key={stock.symbol} // use the stock symbol as the key for each item
              className={`stock-item ${selectedStock === stock.symbol ? 'active' : ''}`} // highlights the selected stock value
              onClick={() => onSelectStock(stock.symbol)} // allow the state stock to change then a ticker is clicked
            >
              <div className="stock-info"> {/* display the stock information for ach ticker*/}
                <span className="stock-symbol">{stock.symbol}</span> {/* display stock symbol */}
                <span className="stock-name">{stock.name}</span> {/* display stock name */}
              </div>
              <div className="stock-price"> {/* display stock price and change */}
                <span className="price">
                  {stock.price > 0 ? `$${stock.price.toFixed(2)}` : '—'} {/* display the stock price, formatted to 2 decimal places, or a dash if price is not available */}
                </span>
                <span className={`change ${stock.change >= 0 ? 'positive' : 'negative'}`}> {/* display the stock change percentage, with a positive or negative class for styling */}
                  {stock.price > 0
                    ? `${stock.change >= 0 ? '+' : ''}${stock.change.toFixed(2)}%` // display the percentage change with a + sign for positive changes, formatted to 2 decimal places, or a dash if price is not available
                    : ''}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Sidebar; // export the Sidebar component for use in app.js
